import type { ErrorCode } from '@tira/protocol';
import type { Phrase } from './copy.js';

/**
 * The shared chrome — the components every screen borrows — plus the two
 * vocabularies that arrive from the server as ids rather than as sentences:
 * error codes and table-talk presets. Both are rendered by id here, so the
 * server never has to know which language this phone is speaking.
 */
export const UI = {
  /* ── Shell ────────────────────────────────────────────────────────────── */
  'ui.boot.failed': { tl: 'Hindi maka-konekta: {reason}', en: 'Can’t connect: {reason}' },
  'ui.rotate': { tl: 'I-landscape mo muna ang telepono.', en: 'Turn your phone sideways first.' },
  'ui.strapline': {
    tl: '18+ · SIMULATED CHIPS · WALANG CASH VALUE',
    en: '18+ · SIMULATED CHIPS · NO CASH VALUE',
  },
  'ui.fullscreen.enter': { tl: 'Buong screen', en: 'Full screen' },
  'ui.fullscreen.exit': { tl: 'Lumabas sa buong screen', en: 'Leave full screen' },

  /* ── Chips ────────────────────────────────────────────────────────────── */
  /*
   * The unit is "chips" in both languages, and stays that way: nothing beside a
   * count may read as money (PRD §7.3). Only the spoken sign carries a word.
   */
  'ui.chips.exact': { tl: '{sign}{n} chips', en: '{sign}{n} chips' },
  'ui.chips.spoken': { tl: '{n} chips', en: '{n} chips' },
  'ui.chips.spoken.minus': { tl: 'minus {n} chips', en: 'minus {n} chips' },
  'ui.chips.spoken.plus': { tl: 'plus {n} chips', en: 'plus {n} chips' },

  /* ── Table chrome ─────────────────────────────────────────────────────── */
  'ui.turn.left': { tl: 'Natitirang oras sa tira', en: 'Time left this turn' },
  'ui.puck.turn': { tl: 'Natitirang oras ni {who}', en: '{who}’s time left' },
  'ui.puck.you': { tl: 'Ikaw', en: 'You' },
  'ui.puck.ai': { tl: 'AI', en: 'AI' },
  'ui.puck.dropped': { tl: 'Nawalan ng signal', en: 'Lost signal' },
  'ui.miron.watching': { tl: 'Nanonood ka bilang miron', en: 'You’re watching, not seated' },
  'ui.pot.label': { tl: 'Pot', en: 'Pot' },
  'ui.pot.tong': { tl: 'Tong (house): {pct}%', en: 'Rake (house): {pct}%' },
  'ui.pot.net': { tl: 'Naipamahagi', en: 'Paid out' },

  /* ── Sheet ────────────────────────────────────────────────────────────── */
  'ui.sheet.close': { tl: 'Isara', en: 'Close' },
  'ui.sheet.close.aria': { tl: 'Isara ang sheet', en: 'Close sheet' },

  /* ── Table talk ───────────────────────────────────────────────────────── */
  'ui.talk.label': { tl: 'Table talk', en: 'Table talk' },
  'ui.talk.muted': { tl: 'Naka-mute ang table talk', en: 'Table talk is muted' },
  'ui.talk.preset.sige': { tl: 'Sige pa!', en: 'Keep going!' },
  'ui.talk.preset.hina': { tl: 'Hina naman', en: 'So weak' },
  'ui.talk.preset.dali': { tl: 'Dali na', en: 'Hurry up' },
  'ui.talk.preset.aray': { tl: 'Aray', en: 'Ouch' },
  'ui.talk.preset.suwerte': { tl: 'Suwerte lang ’yan', en: 'Pure luck' },
  'ui.talk.preset.bawi': { tl: 'Bawi ako', en: 'I’ll get it back' },
  'ui.talk.preset.todas': { tl: 'Todas ka na', en: 'You’re toast' },
  'ui.talk.preset.isapa': { tl: 'Isa pa!', en: 'One more!' },
  'ui.talk.preset.gera': { tl: 'Ge, gera', en: 'Bring it on' },
  'ui.talk.preset.galing': { tl: 'Galing ah', en: 'Nice one' },
  'ui.talk.preset.miron': { tl: 'Miron ka lang', en: 'You’re just watching' },
  'ui.talk.preset.salamat': { tl: 'Salamat sa chips 😏', en: 'Thanks for the chips 😏' },

  /* ── Voice ────────────────────────────────────────────────────────────── */
  'ui.voice.hold': { tl: 'Pindutin, magsalita', en: 'Hold to talk' },
  'ui.voice.denied': {
    tl: 'Naka-block ang mic. Payagan ito sa settings ng device mo.',
    en: 'Mic is blocked. Allow it in your device settings.',
  },

  /* ── Server errors ────────────────────────────────────────────────────── */
  /*
   * One line per ErrorCode. The server still sends its own Taglish `message`,
   * and an unknown code falls back to it — but a code we know reads in the
   * player's language, which is the only way the toast can follow the setting
   * without the server tracking who speaks what. The intersection below makes
   * a new ErrorCode with no line here a compile error.
   */
  'ui.err.unauthenticated': {
    tl: 'Hindi ka nakilala. Mag-sign in ulit.',
    en: 'We lost your session. Sign in again.',
  },
  'ui.err.age_gate_required': {
    tl: 'Sagutin mo muna ang age gate.',
    en: 'Clear the age gate first.',
  },
  'ui.err.insufficient_chips': {
    tl: 'Kulang ang chips mo para dito.',
    en: 'Not enough chips for that.',
  },
  'ui.err.invalid_buyin': {
    tl: 'Wala sa saklaw ang buy-in mo.',
    en: 'That buy-in is out of range.',
  },
  'ui.err.illegal_action': { tl: 'Hindi pwede ang tirang ’yan.', en: 'That move is not allowed.' },
  'ui.err.not_your_turn': { tl: 'Hindi pa ikaw ang tira.', en: 'Not your turn yet.' },
  'ui.err.stale_seq': {
    tl: 'Luma na ang mesa mo. Nag-resync ka muna.',
    en: 'Your table is behind. Resyncing you now.',
  },
  'ui.err.table_full': { tl: 'Puno na ang mesa. Subukan ang isa pa.', en: 'Table is full. Try another.' },
  'ui.err.table_not_found': { tl: 'Wala na ang mesang ’yan.', en: 'That table is gone.' },
  'ui.err.match_not_found': { tl: 'Wala na ang laro na ’yan.', en: 'That game is gone.' },
  'ui.err.rate_limited': {
    tl: 'Dahan-dahan lang. Subukan ulit mamaya.',
    en: 'Easy does it. Try again in a bit.',
  },
  'ui.err.kubo_tier_capped': {
    tl: 'Masyadong mataas ang tier para sa kubo.',
    en: 'That tier is too high for a private room.',
  },
  'ui.err.wallet_frozen': {
    tl: 'Naka-hold ang wallet mo habang nire-review.',
    en: 'Your wallet is on hold while we review it.',
  },
  'ui.err.internal': {
    tl: 'May diperensya sa amin. Subukan ulit.',
    en: 'Something broke on our end. Try again.',
  },
  'fmt.eta.unknown': { tl: 'Hindi pa tiyak', en: 'Not sure yet' },
  'fmt.eta.secs': { tl: '~{n} segundo', en: '~{n} seconds' },
  'fmt.eta.mins': { tl: '~{n} minuto', en: '~{n} minutes' },
  /* Singulars: English inflects after a count and Taglish does not. */
  'ui.chips.spoken.one': { tl: '{n} chips', en: '{n} chip' },
  'fmt.eta.secs.one': { tl: '~{n} segundo', en: '~{n} second' },
  'fmt.eta.mins.one': { tl: '~{n} minuto', en: '~{n} minute' },
} as const satisfies Record<string, Phrase> & Record<`ui.err.${ErrorCode}`, Phrase>;
